import { useState, useEffect, useMemo, useCallback } from 'react';

export interface UseSearchConfig {
  debounceMs?: number;
}

export interface SearchableItem {
  id: string;
  [key: string]: any;
}

export interface SearchField {
  path: string;
  weight?: number; // 검색 가중치 (1-10)
}

export const useSearch = <T extends SearchableItem>(
  items: T[],
  searchFields: SearchField[],
  config: UseSearchConfig = {}
) => {
  const { debounceMs = 300 } = config;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // 디바운스된 검색어 업데이트
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  // 검색어 정규화
  const normalizedSearchTerm = useMemo(() =>
    debouncedSearchTerm.toLowerCase().trim(),
    [debouncedSearchTerm]
  );

  // 중첩된 객체 속성에 안전하게 접근하는 함수
  const getNestedValue = useCallback((obj: any, path: string): string => {
    return path.split('.').reduce((current, key) => {
      return current?.[key] || '';
    }, obj)?.toString().toLowerCase() || '';
  }, []);

  // 필터링된 결과
  const filteredItems = useMemo(() => {
    if (!items?.length) return [];
    if (!normalizedSearchTerm) return items;

    return items.filter(item => {
      if (!item) return false;

      // 각 검색 필드에서 검색어가 포함되어 있는지 확인
      return searchFields.some(field => {
        const value = getNestedValue(item, field.path);
        return value.includes(normalizedSearchTerm);
      });
    });
  }, [items, normalizedSearchTerm, searchFields, getNestedValue]);

  // 검색 결과 통계
  const searchStats = useMemo(() => ({
    total: items?.length || 0,
    filtered: filteredItems.length,
    hasResults: filteredItems.length > 0,
    isSearching: normalizedSearchTerm.length > 0
  }), [items?.length, filteredItems.length, normalizedSearchTerm]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    normalizedSearchTerm,
    filteredItems,
    searchStats,
    handleSearchChange,
    clearSearch,
    setSearchTerm
  };
};